import { toast } from 'sonner';
import { useState } from 'react';
import { KeyRound, Trash2 } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { formatCalendarDate } from '@/lib/utils/date';
import { passkeysSupported, useDeletePasskey, usePasskeys, useRegisterPasskey } from '../api/passkeys';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Props {
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

/**
 * Manage the passkeys on this account.
 *
 * A passkey is added from inside an existing session, never used to claim an
 * account — registration is authenticated, so an attacker with a stolen link
 * cannot attach their own authenticator.
 */
export function PasskeysDialog({ open, onOpenChange }: Props) {
  const { isLoading, data: passkeys } = usePasskeys(open);
  const register = useRegisterPasskey();
  const remove = useDeletePasskey();
  const [name, setName] = useState('');
  const [error, setError] = useState<null | string>(null);

  const supported = passkeysSupported();

  const add = async () => {
    setError(null);
    try {
      await register.mutateAsync(name.trim() || 'Passkey');
      setName('');
      toast.success('Passkey added');
    } catch (err: unknown) {
      const errName = (err as { name?: string })?.name;
      if (errName === 'NotAllowedError' || errName === 'AbortError') return; // dismissed
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      // InvalidStateError means this authenticator already holds a credential
      // for this account — excludeCredentials working as intended, not a fault.
      setError(
        errName === 'InvalidStateError'
          ? 'This device already has a passkey for your account.'
          : (detail ?? 'Could not add that passkey. Please try again.'),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Passkeys</DialogTitle>
          <DialogDescription>
            Sign in with your fingerprint, face or device PIN instead of a password.
          </DialogDescription>
        </DialogHeader>

        {!supported ? (
          <p className="text-sm text-muted-foreground">This browser does not support passkeys.</p>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

              {!isLoading && passkeys?.length === 0 && (
                <p className="text-sm text-muted-foreground">No passkeys yet.</p>
              )}

              {passkeys?.map((passkey) => (
                <div
                  key={passkey.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <KeyRound aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{passkey.name}</p>
                      <p className="text-xs text-muted-foreground">
                        Added {formatCalendarDate(passkey.created_at)}
                        {passkey.last_used_at && ` · last used ${formatCalendarDate(passkey.last_used_at)}`}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    disabled={remove.isPending}
                    aria-label={`Remove ${passkey.name}`}
                    onClick={() => remove.mutate(passkey.id)}>
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="space-y-2 border-t border-border pt-4">
              <Label htmlFor="passkey-name">Name this device</Label>
              <Input
                id="passkey-name"
                value={name}
                placeholder="MacBook, iPhone…"
                onChange={(e) => setName(e.target.value)}
              />
              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {supported && (
            <Button type="button" disabled={register.isPending} onClick={add}>
              {register.isPending ? 'Waiting for your device…' : 'Add a passkey'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
